import React, { useState } from "react";
import {
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Backdrop,
  Box,
  TextField,
  Grid,
} from "@mui/material";
import { Edit, Trash2, AlertTriangle, X, Layers } from "lucide-react";
import { alpha } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { setRefresh } from "../../../../redux/refreshSlice";
import { useDestroy } from "../../../../hooks/useDestroy";
import { useUpdate } from "../../../../hooks/useUpdate";
import { setShowStockBatch } from "../../../../redux/itemUnit/productItemUnitSlice";

const ProductItemUnitInfoListRow = ({ row }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { destroyData, loading } = useDestroy("item-unit-info");
  const { updateData, loading: updateLoading } = useUpdate("unit-info");
  const dispatch = useDispatch();
  const { refresh } = useSelector((state) => state.refresh);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      purchasePrice: row.purchasePrice || 0,
      sellingPrice: row.sellingPrice || 0,
      reOrderQty: row.reOrderQty || 0,
      relatedBranch: row.relatedBranch?.name || "",
      qty: row.qty || 0,
    },
  });

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await destroyData(row._id);
    if (result) {
      dispatch(setRefresh(!refresh));
      setDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const getStockStatus = (currentQty, reorderQty) => {
    if (currentQty === 0) {
      return { label: "Out of Stock", color: "error" };
    } else if (currentQty <= reorderQty) {
      return { label: "Low Stock", color: "warning" };
    } else {
      return { label: "In Stock", color: "success" };
    }
  };

  const stockStatus = getStockStatus(row.qty, row.reOrderQty);

  const handleStockBatch = () => {
    dispatch(setShowStockBatch({ data: row, isShowStockBatch: true }));
  };

  const handleProductInfoEdit = () => {
    reset({
      purchasePrice: row.purchasePrice || 0,
      sellingPrice: row.sellingPrice || 0,
      reOrderQty: row.reOrderQty || 0,
      relatedBranch: row.relatedBranch?.name || "",
      qty: row.qty || 0,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (data) => {
    const payload = {
      purchasePrice: parseFloat(data.purchasePrice),
      sellingPrice: parseFloat(data.sellingPrice),
      reOrderQty: parseInt(data.reOrderQty),
    };

    const result = await updateData(row._id, payload);
    console.log("result", result);
    if (result) {
      dispatch(setRefresh(!refresh));
      setEditModalOpen(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
  };

  return (
    <>
      <TableRow
        key={row._id}
        sx={{
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell component="th" scope="row">
          <Typography variant="body2" fontWeight={500}>
            {row.relatedBranch?.name ?? "N/A"}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={500}>
            {row.purchasePrice.toLocaleString()} MMK
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={500}>
            {row.sellingPrice.toLocaleString()} MMK
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>
            {row.qty}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color="text.secondary">
            {row.reOrderQty}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={stockStatus.label}
            color={stockStatus.color}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title="StockBatch">
              <IconButton size="small" onClick={handleStockBatch}>
                <Layers size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={handleProductInfoEdit}
                size="small"
                sx={{
                  color: "warning.main",
                  "&:hover": {
                    backgroundColor: "warning.main",
                    color: "white",
                  },
                }}
              >
                <Edit size={16} />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title={loading ? "Deleting..." : "Delete"}>
              <IconButton
                size="small"
                onClick={handleDeleteClick}
                disabled={loading}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "white",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Trash2 size={16} />
                )}
              </IconButton>
            </Tooltip> */}
          </Box>
        </TableCell>
      </TableRow>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <X size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 4 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash2 size={24} color="white" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: "white", fontWeight: 600, mb: 0.5 }}
              >
                Delete Product Item Unit Info
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Warning alert with enhanced styling */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              p: 2.5,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 2,
              mb: 3,
            }}
          >
            <AlertTriangle size={20} color="#ef4444" style={{ marginTop: 2 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: "#991b1b", fontWeight: 600, mb: 0.5 }}
              >
                Warning: Permanent Deletion
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#7f1d1d", lineHeight: 1.5 }}
              >
                This action cannot be undone. The product item unit info will be
                permanently deleted from the system.
              </Typography>
            </Box>
          </Box>

          {/* Confirmation message */}
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: "1.1rem" }}
          >
            Are you sure you want to delete this product item unit info?
          </Typography>

          {/* Item details card */}
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#64748b", fontWeight: 600, mb: 1.5 }}
            >
              UNIT INFO TO DELETE
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "#1e293b", fontWeight: 600, mb: 1 }}
            >
              {row?.relatedBranch?.name || "Unknown Branch"}
            </Typography>
            <Box sx={{ display: "flex", gap: 3, mb: 1.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Selling Price
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.sellingPrice?.toLocaleString() || 0} MMK
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Purchase Price
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.purchasePrice?.toLocaleString() || 0} MMK
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#94a3b8", display: "block" }}
                >
                  Quantity
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 500 }}
                >
                  {row?.qty || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Enhanced action buttons */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            startIcon={loading ? null : <Trash2 size={18} />}
            disabled={loading}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
              "&:disabled": {
                backgroundColor: "#fca5a5",
                color: "white",
              },
            }}
          >
            {loading ? "Deleting..." : "Delete Info"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Form Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditCancel}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleEditCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <X size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 4 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Edit size={24} color="white" />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ color: "white", fontWeight: 600, mb: 0.5 }}
              >
                Edit Product Item Unit Info
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                Update pricing and reorder information
              </Typography>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(handleEditSubmit)}>
          <DialogContent sx={{ px: 3, py: 3 }}>
            <Grid container spacing={3}>
              {/* Related Branch (Disabled) */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="relatedBranch"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Related Branch"
                      variant="outlined"
                      fullWidth
                      disabled
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Quantity (Disabled) */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="qty"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Current Quantity"
                      variant="outlined"
                      fullWidth
                      disabled
                      type="number"
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Purchase Price */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="purchasePrice"
                  control={control}
                  rules={{
                    required: "Purchase price is required",
                    min: {
                      value: 0,
                      message:
                        "Purchase price must be greater than or equal to 0",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Purchase Price (MMK)"
                      variant="outlined"
                      fullWidth
                      type="number"
                      error={!!errors.purchasePrice}
                      helperText={errors.purchasePrice?.message}
                      InputProps={{
                        endAdornment: (
                          <Typography sx={{ mr: 1 }}>MMK</Typography>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Selling Price */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sellingPrice"
                  control={control}
                  rules={{
                    required: "Selling price is required",
                    min: {
                      value: 0,
                      message:
                        "Selling price must be greater than or equal to 0",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Selling Price (MMK)"
                      variant="outlined"
                      fullWidth
                      type="number"
                      error={!!errors.sellingPrice}
                      helperText={errors.sellingPrice?.message}
                      InputProps={{
                        endAdornment: (
                          <Typography sx={{ mr: 1 }}>MMK</Typography>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Reorder Quantity */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="reOrderQty"
                  control={control}
                  rules={{
                    required: "Reorder quantity is required",
                    min: {
                      value: 0,
                      message:
                        "Reorder quantity must be greater than or equal to 0",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reorder Quantity"
                      variant="outlined"
                      fullWidth
                      type="number"
                      error={!!errors.reOrderQty}
                      helperText={errors.reOrderQty?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Info Box */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#0369a1", fontWeight: 500, mb: 0.5 }}
              >
                ℹ️ Edit Information
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#0c4a6e", lineHeight: 1.4 }}
              >
                You can only edit Purchase Price, Selling Price, and Reorder
                Quantity. Related Branch and Current Quantity fields are
                disabled for security reasons.
              </Typography>
            </Box>
          </DialogContent>

          {/* Enhanced action buttons */}
          <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 2 }}>
            <Button
              onClick={handleEditCancel}
              variant="outlined"
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={updateLoading ? null : <Edit size={18} />}
              disabled={updateLoading}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "#3b82f6",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
                "&:disabled": {
                  backgroundColor: "#93c5fd",
                  color: "white",
                },
              }}
            >
              {updateLoading ? "Updating..." : "Update Info"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ProductItemUnitInfoListRow;
